import { SKILL_IDS, STRATEGY_IDS } from "../constants.ts";
import { factId, positiveFamilyId } from "../ids.ts";
import type {
  Fact,
  FactCatalog,
  FactFamily,
  Operation,
  ProblemClassId,
  SkillId,
  StrategyId,
} from "../model.ts";

export const FACT_CATALOG_VERSION = "r20-facts-v1";

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function problemClassesFor(
  operation: Operation,
  left: number,
  right: number,
  answer: number,
): ProblemClassId[] {
  if (left === 0 || right === 0 || (operation === "SUBTRACT" && answer === 0)) {
    return ["ZERO_RELATION"];
  }

  if (
    (operation === "ADD" && answer <= 10)
    || (operation === "SUBTRACT" && left <= 10)
  ) {
    return ["WITHIN_10"];
  }

  if (
    operation === "ADD"
    && left <= 9
    && right <= 9
    && answer > 10
  ) {
    return ["CROSS_10_ADD"];
  }

  if (
    operation === "SUBTRACT"
    && left > 10
    && right <= 9
    && answer < 10
  ) {
    return ["CROSS_10_SUB"];
  }

  if (
    operation === "ADD"
      ? answer > 10 && (left >= 10 || right >= 10)
      : left > 10 && right <= 9 && answer >= 10
  ) {
    return ["TEEN_NO_BRIDGE"];
  }

  return ["OTHER_WITHIN_20"];
}

function contentSkillIdsFor(
  operation: Operation,
  left: number,
  right: number,
  answer: number,
  problemClasses: readonly ProblemClassId[],
): SkillId[] {
  if (problemClasses.includes("ZERO_RELATION")) return [SKILL_IDS.ZERO];

  const skills: SkillId[] = [];
  const additionPair = operation === "ADD" ? [left, right] : [right, answer];
  const [familyA, familyB] = [...additionPair].sort((a, b) => a - b);
  const within10 = problemClasses.includes("WITHIN_10");

  if (
    (operation === "ADD" && (left === 1 || right === 1))
    || (operation === "SUBTRACT" && right === 1)
  ) {
    skills.push(SKILL_IDS.PLUS_MINUS_1);
  }

  if (
    (operation === "ADD" && (left === 2 || right === 2))
    || (operation === "SUBTRACT" && right === 2)
  ) {
    skills.push(SKILL_IDS.PLUS_MINUS_2);
  }

  if (familyA === familyB) skills.push(SKILL_IDS.DOUBLES);
  if (left === 10 || answer === 10) skills.push(SKILL_IDS.MAKE_10);
  if (Math.abs(familyA - familyB) === 1) skills.push(SKILL_IDS.NEAR_DOUBLES);

  if (within10 && skills.length === 0) skills.push(SKILL_IDS.BASIC_TO_10);
  if (problemClasses.includes("TEEN_NO_BRIDGE")) skills.push(SKILL_IDS.TEEN_NO_BRIDGE);
  if (problemClasses.includes("CROSS_10_ADD")) skills.push(SKILL_IDS.CROSS_10_ADD);
  if (problemClasses.includes("CROSS_10_SUB")) skills.push(SKILL_IDS.CROSS_10_SUB);

  if (operation === "ADD") skills.push(SKILL_IDS.COMMUTATIVE);
  skills.push(SKILL_IDS.INVERSE);

  return unique(skills);
}

function strategyTagsFor(
  operation: Operation,
  left: number,
  right: number,
  answer: number,
  problemClasses: readonly ProblemClassId[],
): StrategyId[] {
  if (problemClasses.includes("ZERO_RELATION")) return [STRATEGY_IDS.ZERO_IDENTITY];

  const tags: StrategyId[] = [];
  const additionPair = operation === "ADD" ? [left, right] : [right, answer];
  const [familyA, familyB] = [...additionPair].sort((a, b) => a - b);

  if (operation === "ADD" && (left <= 2 || right <= 2)) tags.push(STRATEGY_IDS.COUNT_ON);
  if (operation === "SUBTRACT" && right <= 2) tags.push(STRATEGY_IDS.COUNT_BACK);
  if (familyA === familyB) tags.push(STRATEGY_IDS.DOUBLES);
  if (left === 10 || answer === 10) tags.push(STRATEGY_IDS.MAKE_10);
  if (Math.abs(familyA - familyB) === 1) tags.push(STRATEGY_IDS.NEAR_DOUBLE);
  if (operation === "ADD") tags.push(STRATEGY_IDS.COMMUTE);
  tags.push(STRATEGY_IDS.INVERSE_FACT);

  if (problemClasses.includes("TEEN_NO_BRIDGE")) tags.push(STRATEGY_IDS.TEEN_ANALOGY);
  if (problemClasses.includes("CROSS_10_ADD")) tags.push(STRATEGY_IDS.VIA_10_ADD);
  if (problemClasses.includes("CROSS_10_SUB")) tags.push(STRATEGY_IDS.BACK_VIA_10);

  return unique(tags);
}

function createFact(
  operation: Operation,
  left: number,
  right: number,
  answer: number,
  familyId: string | null,
): Fact {
  const problemClassIds = problemClassesFor(operation, left, right, answer);
  return {
    id: factId(operation, left, right, answer),
    familyId,
    operation,
    left,
    right,
    answer,
    numberRange: 20,
    isZeroRelation: familyId === null,
    problemClassIds,
    skillIds: contentSkillIdsFor(operation, left, right, answer, problemClassIds),
    strategyTags: strategyTagsFor(operation, left, right, answer, problemClassIds),
  };
}

function buildPositiveFamilies(): { families: FactFamily[]; facts: Fact[] } {
  const families: FactFamily[] = [];
  const facts: Fact[] = [];

  for (let a = 1; a <= 20; a += 1) {
    for (let b = a; b <= 20; b += 1) {
      const c = a + b;
      if (c > 20) break;

      const familyId = positiveFamilyId(a, b, c);
      const familyFacts = [
        createFact("ADD", a, b, c, familyId),
        ...(a === b ? [] : [createFact("ADD", b, a, c, familyId)]),
        createFact("SUBTRACT", c, a, b, familyId),
        ...(a === b ? [] : [createFact("SUBTRACT", c, b, a, familyId)]),
      ];

      facts.push(...familyFacts);
      families.push({
        id: familyId,
        kind: "POSITIVE",
        a,
        b,
        c,
        factIds: familyFacts.map((fact) => fact.id),
      });
    }
  }

  return { families, facts };
}

function buildZeroRelationFacts(): Fact[] {
  const facts = new Map<string, Fact>();

  for (let n = 0; n <= 20; n += 1) {
    const candidates = [
      createFact("ADD", 0, n, n, null),
      createFact("ADD", n, 0, n, null),
      createFact("SUBTRACT", n, 0, n, null),
      createFact("SUBTRACT", n, n, 0, null),
    ];

    for (const fact of candidates) facts.set(fact.id, fact);
  }

  return [...facts.values()];
}

export function createFactCatalog(): FactCatalog {
  const positive = buildPositiveFamilies();
  const facts = [...positive.facts, ...buildZeroRelationFacts()];
  return {
    version: FACT_CATALOG_VERSION,
    families: positive.families,
    facts,
    familyById: new Map(positive.families.map((family) => [family.id, family])),
    factById: new Map(facts.map((fact) => [fact.id, fact])),
  };
}

export const FACT_CATALOG = createFactCatalog();
